/**
 * Pure helpers for the All Ideas (Innovate) list. Every filter / sort / merge rule the page
 * uses lives here once.
 */

const SELECTED_FOR_HACKATHON = 'SELECTED_FOR_HACKATHON'

// `created` is serialized by the backend as { timestamp: <epoch ms> }; tolerate plain values too.
export const getCreatedTime = (idea) => {
  const created = idea?.created
  if (created == null) return 0
  if (typeof created === 'number') return created
  if (typeof created === 'object') return Number(created.timestamp) || 0
  const parsed = Date.parse(created)
  return Number.isNaN(parsed) ? 0 : parsed
}

export const hasActiveFilters = ({ statuses = [] }) => statuses.length > 0

/** Order-insensitive comparison of the API filters, used to skip redundant requests. */
export const areFiltersEqual = (a, b) => {
  if (a.statuses.length !== b.statuses.length) return false
  const set = new Set(a.statuses)
  return b.statuses.every((status) => set.has(status))
}

/** Appends `next` to `prev`, skipping ideas already present (pages can overlap). */
export const mergeUniqueById = (prev, next) => {
  const seen = new Set(prev.map((x) => x?.id))
  return [...prev, ...(next || []).filter((x) => x && !seen.has(x.id))]
}

/** Case-insensitive title search (client-side; ideaList has no search parameter). */
export const filterIdeasBySearch = (ideas, searchTerm) => {
  const query = (searchTerm || '').trim().toLowerCase()
  if (!query) return ideas
  return ideas.filter((idea) => (idea?.title || '').toLowerCase().includes(query))
}

/**
 * Same order as the ideaList endpoint: "Selected for Hackathon" first, then newest first.
 * Re-applied here because pages loaded with "Load more" are merged on the client.
 */
export const sortIdeas = (ideas) =>
  [...ideas].sort((a, b) => {
    const aSelected = a?.status === SELECTED_FOR_HACKATHON
    const bSelected = b?.status === SELECTED_FOR_HACKATHON
    if (aSelected !== bSelected) return aSelected ? -1 : 1
    return getCreatedTime(b) - getCreatedTime(a)
  })

/** Ideas to render: search -> sort. */
export const getVisibleIdeas = (ideas, searchTerm) => sortIdeas(filterIdeasBySearch(ideas, searchTerm))